import { PlusIcon, TagIcon } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import {
  quantityPromotionAddLabel,
  type QuantityPromotionCta,
} from '@/shared/utils/quantity-promotion';

interface QuantityPromotionBannerProps {
  cta: QuantityPromotionCta;
  /** Nome do item que o botão repete ("Adicionar mais 1 de X-Tudo (fecha o par)"). */
  itemName?: string | null;
  /** Onde vale: "Vale para: Hambúrguers". Omitido = sem a linha. */
  scope?: string | null;
  onAdd?: () => void;
  disabled?: boolean;
  /** Versão de 1 linha para o rodapé da sacola na loja. */
  compact?: boolean;
  className?: string;
}

/**
 * Promoção por quantidade (PRAT-184): chamada forte no carrinho — fundo na
 * cor principal da loja (`--primary`, laranja da marca por padrão), texto
 * branco e botão branco que repete a linha participante mais barata.
 */
export function QuantityPromotionBanner({
  cta,
  itemName,
  scope,
  onAdd,
  disabled,
  compact,
  className,
}: QuantityPromotionBannerProps) {
  const addLabel = quantityPromotionAddLabel(cta, itemName);

  if (compact) {
    return (
      <div
        data-testid="quantity-promotion-banner"
        role="status"
        className={cn(
          'flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-primary-foreground shadow-md',
          className
        )}
      >
        <TagIcon className="size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-bold leading-tight">{cta.title}</p>
          {scope && (
            <p
              data-testid="quantity-promotion-scope"
              className="truncate text-[10px] font-medium leading-tight opacity-90"
            >
              {scope}
            </p>
          )}
        </div>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            disabled={disabled}
            aria-label={addLabel}
            className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-white px-2.5 text-xs font-bold text-primary shadow-sm transition-transform active:scale-[0.97] disabled:opacity-60 clickable-button"
          >
            <PlusIcon className="size-3.5" />
            Adicionar 1
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="quantity-promotion-banner"
      role="status"
      className={cn(
        'flex flex-col gap-3 rounded-2xl bg-primary p-4 text-primary-foreground shadow-lg',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/20">
          <TagIcon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold leading-tight">{cta.title}</p>
          <p className="mt-0.5 text-xs font-medium opacity-90">{cta.subtitle}</p>
          {scope && (
            <p
              data-testid="quantity-promotion-scope"
              className="mt-1 text-xs font-semibold leading-tight"
            >
              {scope}
            </p>
          )}
        </div>
      </div>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-bold text-primary shadow-sm transition-transform active:scale-[0.98] disabled:opacity-60 clickable-button"
        >
          <PlusIcon className="size-4" />
          <span className="truncate">{addLabel}</span>
        </button>
      )}
    </div>
  );
}
